import { MentionEntityType, IParsedHtmlNode, IEditorBlock, EditorBlockType, IEditorChipToken, IEditorFormatState, MediaSize } from "./types";
import {
  escapeHtml,
  sanitizeHtml as commonSanitizeHtml,
  stripHtml as commonStripHtml,
  isSafeUrl,
  MEMORIES_HTML_POLICY,
  IHtmlSanitizerPolicy,
} from "common247";
export {
  escapeHtml,
  isSafeUrl,
  MEMORIES_HTML_POLICY,
  IHtmlSanitizerPolicy,
};
export const stripHtml = commonStripHtml;

/**
 * Unescapes standard HTML entities back to raw text.
 * @param {string} html - Escaped HTML string.
 * @returns {string} Unescaped raw string.
 */
export function unescapeHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");
}

/**
 * Strips all HTML tags from a string.
 * @param {string} str - HTML string.
 * @returns {string} Plain text string.
 */
export function stripHtmlTags(str: string): string {
  if (!str) return "";
  return str.replace(/<[^>]*>/g, "");
}

/**
 * Preserves allowed formatting and semantic tags while stripping forbidden or unrecognized HTML tags.
 * @param {string} str - Raw input string with possible formatting tags.
 * @returns {string} Sanitized string with allowed formatting tags preserved.
 */
export function sanitizeFormattingHtml(str: string): string {
  if (!str) return "";
  // 1. Strip dangerous script/style/iframe tags
  let cleaned = str.replace(/<(script|iframe|style|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, "");
  // 2. Strip inline event handlers
  cleaned = cleaned.replace(/\son[a-zA-Z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  // 3. Strip Javascript/data URI schemes
  cleaned = cleaned.replace(/(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, "");
  // 4. Preserve allowed tags, strip any other unknown tag wrappers
  cleaned = cleaned.replace(/<\/?([a-zA-Z0-9_-]+)(\s+[^>]*)?>/gi, (match, tag) => {
    const t = tag.toLowerCase();
    if (ALLOWED_TAGS.has(t)) return match;
    return "";
  });
  // 5. Strip Unicode Private Use Area characters (\uE000-\uF8FF) and zero-width spaces/replacement characters
  cleaned = cleaned.replace(/[\uE000-\uF8FF\u200B-\u200D\uFEFF\uFFFC]/g, "");
  return cleaned;
}

/**
 * Maps a character offset in plain text to the corresponding offset in raw HTML/tagged text.
 * @param {string} html - HTML string.
 * @param {number} plainOffset - Offset in plain text.
 * @returns {number} Offset in the raw HTML string.
 */
export function mapPlainOffsetToHtmlOffset(html: string, plainOffset: number): number {
  if (!html || plainOffset <= 0) return 0;
  let plainCount = 0;
  let htmlIdx = 0;
  let inTag = false;

  while (htmlIdx < html.length && plainCount < plainOffset) {
    const char = html[htmlIdx];
    if (char === "<") {
      inTag = true;
    } else if (char === ">" && inTag) {
      inTag = false;
      htmlIdx++;
      continue;
    }

    if (!inTag) {
      plainCount++;
    }
    htmlIdx++;
  }

  // If we arrived at the target plain offset but are right before an opening tag,
  // advance past opening tags so the offset points directly to the text content
  while (htmlIdx < html.length && html[htmlIdx] === "<") {
    const closeIdx = html.indexOf(">", htmlIdx);
    if (closeIdx !== -1 && !html.substring(htmlIdx, closeIdx + 1).startsWith("</")) {
      htmlIdx = closeIdx + 1;
    } else {
      break;
    }
  }

  return htmlIdx;
}

/**
 * Maps a character offset in raw HTML/tagged text to the corresponding offset in plain text.
 * @param {string} html - HTML string.
 * @param {number} htmlOffset - Offset in raw HTML.
 * @returns {number} Offset in the plain text string.
 */
export function mapHtmlOffsetToPlainOffset(html: string, htmlOffset: number): number {
  if (!html || htmlOffset <= 0) return 0;
  const clampedOffset = Math.min(htmlOffset, html.length);
  let plainCount = 0;
  let inTag = false;

  for (let i = 0; i < clampedOffset; i++) {
    const char = html[i];
    if (char === "<") {
      inTag = true;
    } else if (char === ">" && inTag) {
      inTag = false;
      continue;
    }

    if (!inTag) {
      plainCount++;
    }
  }

  return plainCount;
}

/**
 * Restores the browser DOM text selection in a container given plain character offsets.
 * Walks through all text nodes within the container.
 *
 * @param {any} container - The root container DOM element (e.g. contentEditableFlowRef.current).
 * @param {number} plainStart - Start character offset in plain text.
 * @param {number} plainEnd - End character offset in plain text.
 */
export function restoreDomSelection(container: any, plainStart: number, plainEnd: number): void {
  if (typeof window === "undefined" || !window.getSelection || !container) return;
  if (typeof document === "undefined" || !document.createRange) return;

  const sel = window.getSelection();
  if (!sel) return;

  const rootNode = container.nodeType ? container : (container.getScrollableNode?.() || container);
  if (!rootNode) return;

  const startTarget = Math.max(0, Math.min(plainStart, plainEnd));
  const endTarget = Math.max(0, Math.max(plainStart, plainEnd));

  const range = document.createRange();
  let currentOffset = 0;
  let startSet = false;
  let endSet = false;
  let lastTextNode: any = null;

  function traverse(node: any) {
    if (endSet || !node) return;

    // Handle non-editable chips and embeds (contenteditable="false")
    if (node.nodeType === 1) {
      const chipLabel = node.getAttribute?.("data-chip-label") || node.dataset?.chipLabel;
      const isNonEditable =
        Boolean(chipLabel) ||
        node.getAttribute?.("contenteditable") === "false" ||
        node.contentEditable === "false" ||
        node.contentEditable === false;
      if (isNonEditable) {
        lastTextNode = node;
        const len = chipLabel ? chipLabel.length : (node.textContent || "").length;

        if (!startSet && currentOffset >= startTarget) {
          try {
            range.setStartBefore(node);
          } catch {
            const parent = node.parentNode || rootNode;
            const index = Array.prototype.indexOf.call(parent.childNodes, node);
            range.setStart(parent, Math.max(0, index));
          }
          startSet = true;
        } else if (!startSet && currentOffset + len >= startTarget) {
          try {
            range.setStartAfter(node);
          } catch {
            const parent = node.parentNode || rootNode;
            const index = Array.prototype.indexOf.call(parent.childNodes, node);
            range.setStart(parent, index + 1);
          }
          startSet = true;
        }

        if (startSet && !endSet && currentOffset >= endTarget) {
          try {
            range.setEndBefore(node);
          } catch {
            const parent = node.parentNode || rootNode;
            const index = Array.prototype.indexOf.call(parent.childNodes, node);
            range.setEnd(parent, Math.max(0, index));
          }
          endSet = true;
          return;
        } else if (startSet && !endSet && currentOffset + len >= endTarget) {
          try {
            range.setEndAfter(node);
          } catch {
            const parent = node.parentNode || rootNode;
            const index = Array.prototype.indexOf.call(parent.childNodes, node);
            range.setEnd(parent, index + 1);
          }
          endSet = true;
          return;
        }

        currentOffset += len;
        return;
      }
    }

    // Node.TEXT_NODE === 3
    if (node.nodeType === 3) {
      lastTextNode = node;
      const text = node.textContent || "";
      const len = text.length;

      if (!startSet && currentOffset + len >= startTarget) {
        range.setStart(node, Math.max(0, startTarget - currentOffset));
        startSet = true;
      }

      if (startSet && !endSet && currentOffset + len >= endTarget) {
        range.setEnd(node, Math.max(0, Math.min(len, endTarget - currentOffset)));
        endSet = true;
        return;
      }

      currentOffset += len;
    } else if (node.childNodes) {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        traverse(children[i]);
        if (endSet) return;
      }
    }
  }

  traverse(rootNode);

  if (!startSet && lastTextNode) {
    const len = lastTextNode.textContent?.length || 0;
    range.setStart(lastTextNode, len);
    range.setEnd(lastTextNode, len);
    startSet = true;
    endSet = true;
  }

  if (startSet) {
    if (!endSet) {
      range.collapse(true);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  } else {
    // If container has no text nodes at all, anchor on rootNode directly
    try {
      range.selectNodeContents(rootNode);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    } catch {
      // Ignore
    }
  }
}

/**
 * Encodes an entity reference into a clean semantic HTML tag.
 * @param {MentionEntityType} type - The entity type.
 * @param {string} id - The entity unique ID.
 * @param {string} label - The entity display name.
 * @returns {string} Encoded HTML tag string.
 */
export function encodeEntityTag(type: MentionEntityType, id: string, label: string): string {
  const cleanId = escapeHtml(id.trim());
  const cleanLabel = escapeHtml(label.trim());
  return `<${type} id="${cleanId}">${cleanLabel}</${type}>`;
}

/**
 * Set of allowed HTML tag names in lowercase, derived from MEMORIES_HTML_POLICY.
 */
const ALLOWED_TAGS = new Set(MEMORIES_HTML_POLICY.allowedTags);

/**
 * Sanitizes input HTML string using the shared modular MEMORIES_HTML_POLICY from common247.
 * Ensures that no inline styles, executable scripts, event handlers, or dangerous tags can be injected.
 *
 * @param {string} rawHtml - Unsanitized HTML markup.
 * @param {IHtmlSanitizerPolicy} [customPolicy] - Optional custom policy override.
 * @returns {string} Sanitized, safe HTML string.
 */
export function sanitizeHtml(rawHtml: string, customPolicy?: IHtmlSanitizerPolicy): string {
  return commonSanitizeHtml(rawHtml, customPolicy);
}

/**
 * Helper to parse HTML table markup into 2D header and rows data.
 */
function parseTableContent(innerHtml: string): { headers: string[]; rows: string[][] } {
  const headers: string[] = [];
  const rows: string[][] = [];

  // Extract <th>
  const thRegex = /<th\b[^>]*>([\s\S]*?)<\/th>/gi;
  let thMatch: RegExpExecArray | null;
  while ((thMatch = thRegex.exec(innerHtml)) !== null) {
    headers.push(unescapeHtml(stripHtmlTags(thMatch[1])).trim());
  }

  // Extract <tr>
  const trRegex = /<tr\b[^>]*>([\s\S]*?)<\/tr>/gi;
  let trMatch: RegExpExecArray | null;
  while ((trMatch = trRegex.exec(innerHtml)) !== null) {
    const rowHtml = trMatch[1];
    const tdRegex = /<td\b[^>]*>([\s\S]*?)<\/td>/gi;
    const rowCells: string[] = [];
    let tdMatch: RegExpExecArray | null;
    while ((tdMatch = tdRegex.exec(rowHtml)) !== null) {
      rowCells.push(unescapeHtml(stripHtmlTags(tdMatch[1])).trim());
    }
    if (rowCells.length > 0) {
      rows.push(rowCells);
    }
  }

  const defaultHeaders = headers.length > 0 ? headers : ["Column 1", "Column 2"];
  const defaultRows = rows.length > 0 ? rows : [["", ""]];

  return {
    headers: defaultHeaders,
    rows: defaultRows,
  };
}

/**
 * Helper to parse a text block with inline format flags and chips.
 */
function parseTextBlock(innerHtml: string, type: EditorBlockType, id: string): IEditorBlock {
  const trimmed = (innerHtml || "").trim();
  const formats: IEditorFormatState = {};

  // Check if the entire block is wrapped in block-level formatting
  let unwrapped = trimmed;
  let changed = true;
  while (changed) {
    changed = false;
    const bMatch = unwrapped.match(/^<(b|strong)\b[^>]*>([\s\S]*?)<\/\1>$/i);
    if (bMatch) {
      formats.bold = true;
      unwrapped = bMatch[2].trim();
      changed = true;
      continue;
    }
    const iMatch = unwrapped.match(/^<(i|em)\b[^>]*>([\s\S]*?)<\/\1>$/i);
    if (iMatch) {
      formats.italic = true;
      unwrapped = iMatch[2].trim();
      changed = true;
      continue;
    }
    const uMatch = unwrapped.match(/^<u\b[^>]*>([\s\S]*?)<\/u>$/i);
    if (uMatch) {
      formats.underline = true;
      unwrapped = uMatch[1].trim();
      changed = true;
      continue;
    }
    const sMatch = unwrapped.match(/^<(s|strike)\b[^>]*>([\s\S]*?)<\/\1>$/i);
    if (sMatch) {
      formats.strike = true;
      unwrapped = sMatch[2].trim();
      changed = true;
      continue;
    }
    const spanMatch = unwrapped.match(/^<span\b([^>]*)>([\s\S]*?)<\/span>$/i);
    if (spanMatch) {
      const attrs = spanMatch[1];
      const fontM = attrs.match(/\bdata-font=["']([^"']+)["']/i);
      const sizeM = attrs.match(/\bdata-size=["']([^"']+)["']/i);
      if (fontM) formats.fontFamily = fontM[1].trim();
      if (sizeM) formats.fontSize = sizeM[1].trim();
      unwrapped = spanMatch[2].trim();
      changed = true;
      continue;
    }
  }

  // Extract entity chips
  const chips: IEditorChipToken[] = [];
  const entityRegex = /<(contact|group|media|memory)\s+id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))\s*>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  let offset = 0;
  while ((m = entityRegex.exec(unwrapped)) !== null) {
    chips.push({
      entityType: m[1].toLowerCase() as MentionEntityType,
      id: m[2] ?? m[3] ?? m[4] ?? "",
      label: unescapeHtml(stripHtmlTags(m[5])),
      offset: offset++,
    });
  }

  // In block.text, unwrap entity tags to their inner label for clean text representation,
  // while preserving safe inline formatting tags (<b>, <i>, <u>, <s>, <span>, <a>)
  let textContent = unwrapped.replace(/<(contact|group|media|memory)\b[^>]*>([\s\S]*?)<\/\1>/gi, "$2");
  textContent = sanitizeFormattingHtml(textContent)
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .trim();

  return {
    id,
    type,
    text: textContent,
    formats: Object.keys(formats).length > 0 ? formats : undefined,
    chips: chips.length > 0 ? chips : undefined,
  };
}

/**
 * Parses safe or raw HTML into structured, normal-text document blocks (IEditorBlock[]).
 * Normal text editing means all HTML angle brackets are abstracted away:
 * - Headings (h1, h2, h3) become blocks of type 'h1', 'h2', 'h3'.
 * - Paragraphs become blocks of type 'paragraph'.
 * - Lists become 'bullet-list' or 'ordered-list' with items.
 * - Tables become 'table' blocks with interactive cells.
 * - Media embeds become 'media' blocks with in-place sizing options.
 * - Format tags (b, i, u) become format state flags.
 * - Entity tags become structured chips.
 */
export function parseHtmlToBlocks(html: string): IEditorBlock[] {
  if (!html || typeof html !== "string" || !html.trim()) {
    return [
      {
        id: `block-${Date.now()}-0`,
        type: "paragraph",
        text: "",
      },
    ];
  }

  const blocks: IEditorBlock[] = [];
  let blockCounter = 0;
  const nextId = () => `b-${Date.now()}-${blockCounter++}`;

  const blockRegex = /<(table|media|h1|h2|h3|h4|h5|h6|blockquote|ul|ol|p)\b([^>]*)>([\s\S]*?)<\/\1>/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = blockRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      const interstitial = html.substring(lastIndex, match.index).trim();
      if (interstitial) {
        const chunks = interstitial.split(/(?:<br\s*\/?>|\n){2,}/gi);
        for (const chunk of chunks) {
          const trimmed = chunk.trim();
          if (trimmed) {
            blocks.push(parseTextBlock(trimmed, "paragraph", nextId()));
          }
        }
      }
    }

    const tagName = match[1].toLowerCase();
    const attributes = match[2] || "";
    const innerContent = match[3] || "";

    const parseMediaTagAttributes = (attrs: string, inner: string) => {
      const idMatch = attrs.match(/\bid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const mediaId = idMatch ? idMatch[1] ?? idMatch[2] ?? idMatch[3] ?? "" : "";
      const sizeMatch = attrs.match(/\bsize\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const size = (sizeMatch ? sizeMatch[1] ?? sizeMatch[2] ?? sizeMatch[3] ?? "50%" : "50%") as MediaSize;
      const widthMatch = attrs.match(/\bwidth\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const rawWidth = widthMatch ? widthMatch[1] ?? widthMatch[2] ?? widthMatch[3] : undefined;
      const width = rawWidth ? (rawWidth.endsWith("%") ? rawWidth : parseInt(rawWidth, 10) || undefined) : undefined;
      const heightMatch = attributes.match(/\bheight\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const rawHeight = heightMatch ? heightMatch[1] ?? heightMatch[2] ?? heightMatch[3] : undefined;
      const height = rawHeight ? Math.max(80, Math.min(800, parseInt(rawHeight, 10) || 180)) : undefined;
      const alignMatch = attrs.match(/\balign\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const align = (alignMatch ? alignMatch[1] ?? alignMatch[2] ?? alignMatch[3] ?? undefined : undefined) as ("left" | "right" | "center" | undefined);
      const title = unescapeHtml(stripHtmlTags(inner)).trim() || "Embedded Media";
      return { mediaId, title, size, width, height, align };
    };

    if (tagName === "table") {
      const tableData = parseTableContent(innerContent);
      blocks.push({
        id: nextId(),
        type: "table",
        text: "",
        table: tableData,
      });
    } else if (tagName === "media") {
      const mediaData = parseMediaTagAttributes(attributes, innerContent);
      blocks.push({
        id: nextId(),
        type: "media",
        text: mediaData.title,
        media: mediaData,
      });
    } else if (tagName === "ul" || tagName === "ol") {
      const itemRegex = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
      const items: string[] = [];
      let itemMatch: RegExpExecArray | null;
      while ((itemMatch = itemRegex.exec(innerContent)) !== null) {
        items.push(unescapeHtml(stripHtmlTags(itemMatch[1])).trim());
      }
      blocks.push({
        id: nextId(),
        type: tagName === "ul" ? "bullet-list" : "ordered-list",
        text: items.map((it) => (tagName === "ul" ? `• ${it}` : it)).join("\n"),
        listItems: items.length > 0 ? items : [""],
      });
    } else {
      const blockType: EditorBlockType =
        tagName === "h1" || tagName === "h2" || tagName === "h3"
          ? (tagName as EditorBlockType)
          : tagName === "blockquote"
          ? "quote"
          : "paragraph";

      const embeddedMediaRegex = /<media\b([^>]*)>([\s\S]*?)<\/media>/gi;
      let mMatch: RegExpExecArray | null;
      let lastEmbeddedIdx = 0;
      let hasEmbeddedMedia = false;

      while ((mMatch = embeddedMediaRegex.exec(innerContent)) !== null) {
        hasEmbeddedMedia = true;
        if (mMatch.index > lastEmbeddedIdx) {
          const preChunk = innerContent.substring(lastEmbeddedIdx, mMatch.index).trim();
          if (preChunk) {
            blocks.push(parseTextBlock(preChunk, blockType, nextId()));
          }
        }
        const mediaData = parseMediaTagAttributes(mMatch[1] || "", mMatch[2] || "");
        blocks.push({
          id: nextId(),
          type: "media",
          text: mediaData.title,
          media: mediaData,
        });
        lastEmbeddedIdx = embeddedMediaRegex.lastIndex;
      }

      if (hasEmbeddedMedia) {
        if (lastEmbeddedIdx < innerContent.length) {
          const postChunk = innerContent.substring(lastEmbeddedIdx).trim();
          if (postChunk) {
            blocks.push(parseTextBlock(postChunk, blockType, nextId()));
          }
        }
      } else {
        blocks.push(parseTextBlock(innerContent, blockType, nextId()));
      }
    }

    lastIndex = blockRegex.lastIndex;
  }

  if (lastIndex < html.length) {
    const trailing = html.substring(lastIndex).trim();
    if (trailing) {
      const chunks = trailing.split(/(?:<br\s*\/?>|\n){2,}/gi);
      for (const chunk of chunks) {
        const trimmed = chunk.trim();
        if (trimmed) {
          blocks.push(parseTextBlock(trimmed, "paragraph", nextId()));
        }
      }
    }
  }

  if (blocks.length === 0) {
    blocks.push({
      id: nextId(),
      type: "paragraph",
      text: "",
    });
  }

  return blocks;
}

/**
 * Serializes normal-text editor blocks back to safe, sanitized HTML markup.
 */
export function serializeBlocksToHtml(blocks: IEditorBlock[]): string {
  if (!blocks || blocks.length === 0) return "";

  const htmlParts: string[] = [];

  for (const block of blocks) {
    if (block.type === "table") {
      const headers = block.table?.headers || ["Column 1", "Column 2"];
      const rows = block.table?.rows || [["", ""]];
      const thHtml = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("");
      const trHtml = rows
        .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`)
        .join("");
      htmlParts.push(`<table><thead><tr>${thHtml}</tr></thead><tbody>${trHtml}</tbody></table>`);
      continue;
    }

    if (block.type === "media" && block.media) {
      const size = block.media.size || "50%";
      const widthAttr = block.media.width !== undefined && block.media.width !== null ? ` width="${block.media.width}"` : "";
      const heightAttr = block.media.height ? ` height="${block.media.height}"` : "";
      const alignAttr = block.media.align ? ` align="${block.media.align}"` : "";
      const title = block.media.title || block.text || "Media";
      htmlParts.push(`<media id="${escapeHtml(block.media.mediaId)}" size="${size}"${widthAttr}${heightAttr}${alignAttr}>${escapeHtml(title)}</media>`);
      continue;
    }

    if (block.type === "bullet-list" || block.type === "ordered-list") {
      const tag = block.type === "bullet-list" ? "ul" : "ol";
      const items = block.listItems && block.listItems.length > 0
        ? block.listItems
        : block.text
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*|^\d+[\.\)]\s*/, "").trim())
            .filter(Boolean);

      if (items.length > 0) {
        const liHtml = items.map((it) => `<li>${escapeHtml(it)}</li>`).join("");
        htmlParts.push(`<${tag}>${liHtml}</${tag}>`);
      }
      continue;
    }

    let content = escapeHtml(block.text);

    // Embed chips into content if present
    if (block.chips && block.chips.length > 0) {
      const seenChipIds = new Set<string>();
      for (const chip of block.chips) {
        if (!chip.label || seenChipIds.has(chip.id)) continue;
        seenChipIds.add(chip.id);
        const chipHtml = `<${chip.entityType} id="${escapeHtml(chip.id)}">${escapeHtml(chip.label)}</${chip.entityType}>`;
        // Prevent recursive tag nesting: if content already contains this entity tag for this ID, do not re-wrap
        if (content.includes(`<${chip.entityType} id="${escapeHtml(chip.id)}"`)) {
          continue;
        }
        if (content.includes(chip.label)) {
          content = content.replace(chip.label, chipHtml);
        } else {
          content = content ? `${content} ${chipHtml}` : chipHtml;
        }
      }
    }

    // Preserve sanitized anchor links if present in content
    content = content.replace(/&lt;a\s+href=&quot;([^&]*)&quot;(?:\s+title=&quot;([^&]*)&quot;)?&gt;([\s\S]*?)&lt;\/a&gt;/gi, (_, href, title, label) => {
      const titleAttr = title ? ` title="${title}"` : "";
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${label}</a>`;
    });

    // Restore sanitized inline formatting tags if present in content
    content = content
      .replace(/&lt;(\/)?(b|strong|i|em|u|s|strike)&gt;/gi, "<$1$2>")
      .replace(/&lt;span\s+data-font=&quot;([^&]*)&quot;&gt;([\s\S]*?)&lt;\/span&gt;/gi, '<span data-font="$1">$2</span>')
      .replace(/&lt;span\s+data-size=&quot;([^&]*)&quot;&gt;([\s\S]*?)&lt;\/span&gt;/gi, '<span data-size="$1">$2</span>');

    if (block.formats?.bold) {
      content = `<b>${content}</b>`;
    }
    if (block.formats?.italic) {
      content = `<i>${content}</i>`;
    }
    if (block.formats?.underline) {
      content = `<u>${content}</u>`;
    }
    if (block.formats?.strike) {
      content = `<s>${content}</s>`;
    }
    if (block.formats?.fontFamily) {
      content = `<span data-font="${escapeHtml(block.formats.fontFamily)}">${content}</span>`;
    }
    if (block.formats?.fontSize) {
      content = `<span data-size="${escapeHtml(block.formats.fontSize)}">${content}</span>`;
    }

    if (!content && block.type === "paragraph") {
      continue;
    }

    switch (block.type) {
      case "h1":
        htmlParts.push(`<h1>${content}</h1>`);
        break;
      case "h2":
        htmlParts.push(`<h2>${content}</h2>`);
        break;
      case "h3":
        htmlParts.push(`<h3>${content}</h3>`);
        break;
      case "quote":
        htmlParts.push(`<blockquote>${content}</blockquote>`);
        break;
      case "paragraph":
      default: {
        const paragraphs = content.split(/(?:\r?\n){2,}/);
        if (paragraphs.length > 1) {
          for (const p of paragraphs) {
            if (p.trim()) {
              htmlParts.push(`<p>${p.trim()}</p>`);
            }
          }
        } else {
          htmlParts.push(`<p>${content}</p>`);
        }
        break;
      }
    }
  }

  return sanitizeHtml(htmlParts.join("\n"));
}

/**
 * Tokenizes HTML into structured nodes including custom entity chips, tables, media embeds, and text chunks.
 *
 * @param {string} html - Clean sanitized HTML string.
 * @returns {IParsedHtmlNode[]} Array of parsed nodes for rendering.
 */
export function parseHtmlNodes(html: string): IParsedHtmlNode[] {
  if (!html) return [];

  const nodes: IParsedHtmlNode[] = [];

  // Match tables, media tags, and entity tags
  const complexRegex = /<(table)\b[^>]*>([\s\S]*?)<\/table>|<(media)\b([^>]*)>([\s\S]*?)<\/media>|<(contact|group|memory)\s+id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))\s*>([\s\S]*?)<\/\6>/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = complexRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      const textChunk = html.substring(lastIndex, match.index);
      const chunkNodes = parseStandardHtmlChunk(textChunk);
      for (let i = 0; i < chunkNodes.length; i++) {
        nodes.push(chunkNodes[i]);
      }
    }

    if (match[1] === "table") {
      const tableData = parseTableContent(match[2] || "");
      nodes.push({
        type: "table",
        tableData,
      });
    } else if (match[3] === "media") {
      const attrs = match[4] || "";
      const idMatch = attrs.match(/\bid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const idVal = idMatch ? idMatch[1] ?? idMatch[2] ?? idMatch[3] ?? "" : "";
      const sizeMatch = attrs.match(/\bsize\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const sizeVal = (sizeMatch ? sizeMatch[1] ?? sizeMatch[2] ?? sizeMatch[3] ?? "50%" : "50%") as MediaSize;
      const widthMatch = attrs.match(/\bwidth\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const rawWidth = widthMatch ? widthMatch[1] ?? widthMatch[2] ?? widthMatch[3] : undefined;
      const widthVal = rawWidth ? (rawWidth.endsWith("%") ? rawWidth : parseInt(rawWidth, 10) || undefined) : undefined;
      const heightMatch = attrs.match(/\bheight\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const rawHeight = heightMatch ? heightMatch[1] ?? heightMatch[2] ?? heightMatch[3] : undefined;
      const heightVal = rawHeight ? Math.max(80, Math.min(800, parseInt(rawHeight, 10) || 180)) : undefined;
      const alignMatch = attrs.match(/\balign\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const alignVal = (alignMatch ? alignMatch[1] ?? alignMatch[2] ?? alignMatch[3] ?? "left" : "left") as "left" | "right" | "center";
      const labelVal = unescapeHtml(stripHtmlTags(match[5] || ""));
      nodes.push({
        type: "media-embed",
        id: idVal,
        label: labelVal,
        size: sizeVal,
        width: widthVal,
        height: heightVal,
        align: alignVal,
      });
    } else {
      const entityType = (match[6] || "").toLowerCase() as MentionEntityType;
      const idVal = match[7] ?? match[8] ?? match[9] ?? "";
      const labelVal = unescapeHtml(stripHtmlTags(match[10] || ""));
      nodes.push({
        type: "chip",
        entityType,
        id: idVal,
        label: labelVal,
      });
    }

    lastIndex = complexRegex.lastIndex;
  }

  if (lastIndex < html.length) {
    const textChunk = html.substring(lastIndex);
    const chunkNodes = parseStandardHtmlChunk(textChunk);
    for (let i = 0; i < chunkNodes.length; i++) {
      nodes.push(chunkNodes[i]);
    }
  }

  return nodes;
}

/**
 * Parses standard text and basic markup (<p>, <br>, <b>, <i>, <h1>-<h6>, <li>) into nodes.
 * @param {string} chunk - HTML segment without custom entity chips.
 * @returns {IParsedHtmlNode[]} Array of nodes.
 */
function parseStandardHtmlChunk(chunk: string): IParsedHtmlNode[] {
  if (!chunk) return [];

  const result: IParsedHtmlNode[] = [];
  const tagRegex = /<\/?[a-zA-Z0-9_-]+(\s+[^>]*)?>/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  const activeFormats: IEditorFormatState = {};

  while ((m = tagRegex.exec(chunk)) !== null) {
    if (m.index > lastIdx) {
      const plain = unescapeHtml(chunk.substring(lastIdx, m.index));
      if (plain) {
        result.push({
          type: "text",
          rawText: plain,
          formats: Object.keys(activeFormats).length > 0 ? { ...activeFormats } : undefined,
        });
      }
    }

    const rawTag = m[0];
    const isClose = rawTag.startsWith("</");
    const tagNameMatch = rawTag.match(/<\/?([a-zA-Z0-9_-]+)/);
    const tagName = tagNameMatch ? tagNameMatch[1].toLowerCase() : "";

    if (tagName === "b" || tagName === "strong") {
      if (isClose) delete activeFormats.bold;
      else activeFormats.bold = true;
    } else if (tagName === "i" || tagName === "em") {
      if (isClose) delete activeFormats.italic;
      else activeFormats.italic = true;
    } else if (tagName === "u") {
      if (isClose) delete activeFormats.underline;
      else activeFormats.underline = true;
    } else if (tagName === "s" || tagName === "strike") {
      if (isClose) delete activeFormats.strike;
      else activeFormats.strike = true;
    }

    if (tagName === "br") {
      result.push({ type: "br" });
    } else if (isClose) {
      result.push({ type: "tag-close", tagName });
    } else {
      result.push({ type: "tag-open", tagName });
    }

    lastIdx = tagRegex.lastIndex;
  }

  if (lastIdx < chunk.length) {
    const plain = unescapeHtml(chunk.substring(lastIdx));
    if (plain) {
      result.push({
        type: "text",
        rawText: plain,
        formats: Object.keys(activeFormats).length > 0 ? { ...activeFormats } : undefined,
      });
    }
  }

  return result;
}

/**
 * Resolves media dimensions according to the precedence rules:
 * - If width is set, use width. Otherwise use "size".
 * - If size is set, it dictates the width, and also dictates height proportionally unless height is explicitly set.
 * - Otherwise use explicit width and height.
 */
export function resolveMediaDimensions(options: {
  size?: MediaSize;
  width?: number | string;
  height?: number | string;
}): {
  width: string | number;
  height: number;
  size: MediaSize;
} {
  const sizeVal = options.size || (options.width && String(options.width).endsWith("%") ? (options.width as MediaSize) : "50%");
  const match = String(sizeVal).match(/^(\d+(?:\.\d+)?)%$/);
  const sizePct = match ? Math.min(100, Math.max(20, Math.round(parseFloat(match[1])))) : 50;

  let finalWidth: string | number = sizeVal;
  if (options.width !== undefined && options.width !== null && options.width !== "") {
    finalWidth = options.width;
  } else {
    finalWidth = sizeVal;
  }

  let finalHeight = 180;
  if (options.height !== undefined && options.height !== null && options.height !== "") {
    finalHeight = typeof options.height === "number" ? options.height : parseInt(String(options.height), 10) || 180;
  } else if (sizeVal) {
    finalHeight = Math.round((180 * sizePct) / 50);
  }

  return {
    width: finalWidth,
    height: Math.max(60, Math.min(1200, finalHeight)),
    size: sizeVal,
  };
}

/**
 * Safely extracts clean document plain text and caret offset from contentEditable container.
 *
 * Crucially:
 * - Reads text nodes that belong to actual content.
 * - For non-editable chip elements (contenteditable="false" or [data-chip-label]), extracts solely
 *   their semantic label without traversing their internal DOM (ignoring icon glyphs, close buttons,
 *   and internal div wrapper linebreaks).
 * - Tracks the exact plain character offset at the caret position.
 *
 * @param {any} container - Root contenteditable container element.
 * @param {any} [targetCaretNode] - Node where caret is located.
 * @param {number} [targetCaretOffset] - Offset within caretNode.
 * @returns {{ text: string; caretOffset: number }} Clean document text and caret offset.
 */
export function extractEditorPlainTextAndOffset(
  container: any,
  targetCaretNode?: any,
  targetCaretOffset?: number
): { text: string; caretOffset: number } {
  if (!container) return { text: "", caretOffset: 0 };

  let text = "";
  let caretOffset = 0;
  let caretFound = false;

  const root = container.nodeType ? container : (container.getScrollableNode?.() || container);

  function walk(node: any) {
    if (!node) return;

    // 1. Non-editable chips & widgets
    if (node.nodeType === 1) {
      const el = node as HTMLElement;
      const chipLabel = el.getAttribute?.("data-chip-label") || (el as any).dataset?.chipLabel;
      const hasChipLabel = chipLabel !== null && chipLabel !== undefined && chipLabel !== "";
      const isNonEditable =
        hasChipLabel ||
        el.getAttribute?.("contenteditable") === "false" ||
        el.contentEditable === "false" ||
        (el as any).contentEditable === false;

      if (isNonEditable) {
        const label = chipLabel || "";
        // If caret is on or inside this chip, mark caret found at end of chip
        if (!caretFound && targetCaretNode && (node === targetCaretNode || (node.contains && node.contains(targetCaretNode)))) {
          caretOffset = text.length + label.length;
          caretFound = true;
        }

        // Only append the clean semantic label
        if (label) {
          text += label;
        }
        return; // DO NOT descend into internal chip icons or buttons!
      }
    }

    // 2. Text nodes
    if (node.nodeType === 3) {
      const rawNodeText = node.textContent || "";
      // Strip any PUA icon glyphs (\uE000-\uF8FF) and zero-width spaces/replacement chars (\u200B-\u200D\uFEFF\uFFFC)
      const cleanNodeText = rawNodeText.replace(/[\uE000-\uF8FF\u200B-\u200D\uFEFF\uFFFC]/g, "");
      if (!caretFound && targetCaretNode && node === targetCaretNode) {
        const safeRawOffset = Math.min(Math.max(0, targetCaretOffset || 0), rawNodeText.length);
        const prefixRaw = rawNodeText.substring(0, safeRawOffset);
        const prefixClean = prefixRaw.replace(/[\uE000-\uF8FF\u200B-\u200D\uFEFF\uFFFC]/g, "");
        caretOffset = text.length + prefixClean.length;
        caretFound = true;
      }
      text += cleanNodeText;
      return;
    }

    // 3. Container element: walk children
    if (node.childNodes) {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        if (!caretFound && targetCaretNode && node === targetCaretNode && i === targetCaretOffset) {
          caretOffset = text.length;
          caretFound = true;
        }
        walk(children[i]);
      }
      if (!caretFound && targetCaretNode && node === targetCaretNode && children.length === targetCaretOffset) {
        caretOffset = text.length;
        caretFound = true;
      }
    }
  }

  walk(root);

  if (!caretFound) {
    caretOffset = text.length;
  }

  return { text, caretOffset };
}

